import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../shared/formatMoney';
import { TYPE_LABELS } from './driverTypes';
import DriverNav from './DriverNav';
import { HiExclamationCircle, HiCurrencyDollar } from 'react-icons/hi';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const DriverStatement = () => {
    const { user } = useAuth();
    const driverId = user?.linked_entity_id;

    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const pad = n => String(n).padStart(2, '0');
    const defaultFrom = `${firstOfMonth.getFullYear()}-${pad(firstOfMonth.getMonth() + 1)}-${pad(firstOfMonth.getDate())}`;
    const defaultTo   = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

    const [dateFrom, setDateFrom] = useState(defaultFrom);
    const [dateTo, setDateTo] = useState(defaultTo);
    const [txns, setTxns] = useState([]);
    const [currentBalance, setCurrentBalance] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = async () => {
        if (!driverId) return;
        if (dateFrom > dateTo) { setError('Start date must be before end date.'); return; }
        setLoading(true);
        setError(null);
        try {
            const [txnRes, driverRes] = await Promise.allSettled([
                axios.get(`${rootUrl}/drivers/${driverId}/transactions`, {
                    params: { date_from: dateFrom, date_to: dateTo, order: 'asc' }
                }),
                axios.get(`${rootUrl}/drivers/${driverId}`),
            ]);
            if (txnRes.status === 'fulfilled') {
                setTxns(txnRes.value.data?.data || []);
            } else {
                throw new Error(txnRes.reason?.response?.data?.error || txnRes.reason?.response?.data?.message || 'Failed to load statement.');
            }
            if (driverRes.status === 'fulfilled') {
                setCurrentBalance(driverRes.value.data?.bal);
            }
        } catch (err) {
            setError(err.message || 'Failed to load statement.');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    if (!driverId) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-20">
            No driver account linked.
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="bg-orange-500 px-4 pt-10 pb-6">
                <h1 className="text-white text-xl font-bold">Statement</h1>
            </div>

            <div className="px-4 py-4 space-y-3">
                {/* Current balance */}
                {currentBalance !== undefined && (
                    <div className="bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <HiCurrencyDollar className="w-5 h-5 text-orange-500" />
                            <span className="text-sm font-semibold text-gray-700">Current Balance</span>
                        </div>
                        <span className={`font-bold text-lg ${Number(currentBalance) < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                            {formatMoney(currentBalance)}
                        </span>
                    </div>
                )}

                {/* Date filters */}
                <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">From</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">To</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
                    >
                        Go
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : txns.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-400 text-sm">
                        No transactions for this period.
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <ul className="divide-y divide-gray-50">
                            {txns.map((txn, i) => {
                                return (
                                    <li key={txn.id || i} className="px-4 py-3 flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800">{TYPE_LABELS[txn.type] || txn.type}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">{fmt(txn.date)}</p>
                                            {txn.your_ref && <p className="text-xs text-gray-600 mt-0.5">{txn.your_ref}</p>}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className={`text-sm font-semibold ${Number(txn.amount) < 0 ? 'text-red-500' : 'text-gray-800'}`}>
                                                {formatMoney(txn.amount)}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">Bal: {formatMoney(txn.post_balance)}</p>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>

            <DriverNav />
        </div>
    );
};

export default DriverStatement;
